import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao } from '../../componentes/base';
import { Alternador, AreaSensivel, CampoData } from '../../componentes/formulario';
import { Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { mensagemDeErro, post } from '../../nucleo/api';
import { dinheiro } from '../../nucleo/formato';
import { hojeLocal, invalidarContas, type Baixa, type DocumentoDetalhe } from './tipos';

/**
 * Fase 2 (W24, K9, F4): "Estornar baixa" na ficha do documento (`POST /contas/documentos/:id/estornar-baixa`, permissão
 * `contas.baixar`). O lançamento da baixa (e o da tarifa) é estornado no Financeiro na data escolhida, o documento volta a
 * em aberto ou parcial com o saldo e nada é apagado. Motivo obrigatório; a chave de idempotência nasce com o modal.
 */
export function EstornarBaixa({ doc, baixa, aoFechar }: { doc: DocumentoDetalhe | null; baixa: Baixa | null; aoFechar: () => void }) {
  if (!doc || !baixa) return null;
  return <Corpo doc={doc} baixa={baixa} aoFechar={aoFechar} />;
}

function Corpo({ doc, baixa, aoFechar }: { doc: DocumentoDetalhe; baixa: Baixa; aoFechar: () => void }) {
  const { t } = useTranslation('contas');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const [chave] = useState(() => crypto.randomUUID());
  const [data, setData] = useState(hojeLocal());
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  // Fase 2 (W28): baixa que veio do Asaas pode devolver o dinheiro ao cliente pelo Asaas (desligado por padrão).
  // Trilha 2: a do Pix Sicredi devolve pelo Sicredi (o mesmo campo `devolver_no_asaas`, "devolver pelo provedor").
  const doSicredi = baixa.origem === 'sicredi';
  const doAsaas = baixa.origem === 'asaas' || doSicredi;
  const textos = doSicredi ? { rotulo: 'asaas.devolverSicredi', sim: 'asaas.devolverSicrediSim', nao: 'asaas.devolverSicrediNao' } : { rotulo: 'asaas.devolver', sim: 'asaas.devolverSim', nao: 'asaas.devolverNao' };
  const [devolver, setDevolver] = useState(false);
  const estornar = useMutation({
    mutationFn: () =>
      post(`/contas/documentos/${doc.id}/estornar-baixa`, { baixa_id: baixa.id, data, motivo: motivo.trim(), ...(doAsaas ? { devolver_no_asaas: devolver } : {}) }, { 'Idempotency-Key': chave }),
    onSuccess: async () => {
      await invalidarContas(qc);
      toast.sucesso(t('w24.estornada'));
      aoFechar();
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  });
  const tarifa = baixa.tarifa_centavos ?? 0;
  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t('w24.estornoTitulo', { numero: doc.numero })}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.voltar')}
          </Botao>
          <Botao variante="perigo" disabled={!motivo.trim() || !/^\d{4}-\d{2}-\d{2}$/.test(data)} carregando={estornar.isPending} onClick={() => (setErro(null), estornar.mutate())}>
            {t('w24.estornar')}
          </Botao>
        </>
      }
    >
      <p className="mb-3 text-sm text-tinta-2" data-estornar-baixa>
        {t('w24.estornoTexto', { valor: dinheiro(baixa.total_centavos, baixa.moeda) })}
        {tarifa > 0 ? ` ${t('w24.estornoTarifa', { valor: dinheiro(tarifa, baixa.moeda) })}` : ''}
      </p>
      <div className="flex flex-col gap-3">
        <CampoData type="date" rotulo={t('w24.estornoData')} value={data} onChange={(e) => setData(e.target.value)} />
        <label className="flex flex-col gap-1.5 text-sm font-medium text-tinta-2">
          {t('w24.estornoMotivo')}
          <AreaSensivel rows={2} maxLength={300} value={motivo} onChange={(e) => setMotivo(e.target.value)} className="w-full rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco" />
        </label>
        {!motivo.trim() && <p className="text-xs text-tinta-3">{t('w24.motivoObrigatorio')}</p>}
        {doAsaas && (
          <div className="flex flex-col gap-1">
            <label className="flex items-center justify-between gap-3 text-sm">
              <span className="font-medium text-tinta-2">{t(textos.rotulo)}</span>
              <Alternador marcado={devolver} aoMudar={setDevolver} rotulo={t(textos.rotulo)} />
            </label>
            <p className="text-xs text-tinta-3">{t(devolver ? textos.sim : textos.nao)}</p>
          </div>
        )}
        {erro && (
          <p role="alert" className="rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
            {erro}
          </p>
        )}
      </div>
    </Modal>
  );
}
